import { createApiRef, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ReveniumApi {
  getMe(): Promise<{ userId: string; email: string }>;
  getProducts(): Promise<any[]>;
  getProduct(id: string): Promise<any>;
  getMySubscriptions(productId?: string): Promise<any>;
  getMyCredentials(productId: string): Promise<any>;
  getMyInvoices(): Promise<any>;
  getMyPeriodCharges(productId: string, start: string, end: string): Promise<any[]>;
  getMyMetrics(subscriptionId: string, start: string, end: string): Promise<any>;
  getBilledAmount(subscriptionId: string): Promise<any>;
  getQuotaConsumed(subscriptionId: string): Promise<any>;
  requestAccess(productId: string, acceptedTerms: boolean): Promise<any>;
  chat(productId: string, messages: ChatMessage[]): Promise<string>;
}

export const reveniumApiRef = createApiRef<ReveniumApi>({ id: 'plugin.revenium.api' });

export class ReveniumOfflineError extends Error {
  name = 'ReveniumOfflineError';
}

export class ReveniumClient implements ReveniumApi {
  constructor(
    private readonly deps: { discoveryApi: DiscoveryApi; fetchApi: FetchApi },
  ) {}

  private async call(path: string, init?: RequestInit): Promise<any> {
    const baseUrl = await this.deps.discoveryApi.getBaseUrl('revenium');
    const res = await this.deps.fetchApi.fetch(`${baseUrl}${path}`, {
      headers: { 'Content-Type': 'application/json' },
      ...init,
    });
    if (res.status === 503) {
      throw new ReveniumOfflineError((await res.json()).error ?? 'not configured');
    }
    if (!res.ok) {
      throw new Error(`Revenium request failed (${res.status}): ${await res.text()}`);
    }
    return res.json();
  }

  getMe() {
    return this.call('/me');
  }
  getProducts() {
    return this.call('/products');
  }
  getProduct(id: string) {
    return this.call(`/products/${encodeURIComponent(id)}`);
  }
  getMySubscriptions(productId?: string) {
    return this.call(
      `/me/subscriptions${
        productId ? `?productId=${encodeURIComponent(productId)}` : ''
      }`,
    );
  }
  getMyCredentials(productId: string) {
    return this.call(`/me/credentials?productId=${encodeURIComponent(productId)}`);
  }
  getMyInvoices() {
    return this.call('/me/invoices');
  }
  getMyPeriodCharges(productId: string, start: string, end: string) {
    return this.call(
      `/me/period-charges?productId=${encodeURIComponent(
        productId,
      )}&start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
    );
  }
  getMyMetrics(subscriptionId: string, start: string, end: string) {
    return this.call(
      `/me/metrics?subscriptionId=${encodeURIComponent(
        subscriptionId,
      )}&start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
    );
  }
  getBilledAmount(subscriptionId: string) {
    return this.call(
      `/subscriptions/${encodeURIComponent(subscriptionId)}/billed-amount`,
    );
  }
  getQuotaConsumed(subscriptionId: string) {
    return this.call(
      `/subscriptions/${encodeURIComponent(subscriptionId)}/quota-consumed`,
    );
  }
  requestAccess(productId: string, acceptedTerms: boolean) {
    return this.call('/me/subscriptions', {
      method: 'POST',
      body: JSON.stringify({ productId, acceptedTerms }),
    });
  }
  async chat(productId: string, messages: ChatMessage[]) {
    const data = await this.call('/chat', {
      method: 'POST',
      body: JSON.stringify({ productId, messages }),
    });
    return data.reply as string;
  }
}
